import { Box3, BoxGeometry, Mesh, Vector3, type Material } from 'three';
import { BodyFacing } from './player/facing';
import { installSaves } from './saves';
import { SurvivalGlue } from './player/survivalGlue';
import type { Game } from '../core/game';
import { cameraFeel, health, movement, stamina } from '../content/data/player';
import { ballistics, meleeDefs, shove, weaponDefs } from '../content/data/weapons';
import { horde, senses, zombieDefs } from '../content/data/zombies';
import { fxTuning } from '../content/data/fx';
import { atmosphere } from '../content/data/sounds';
import { surfaceOf } from '../world/surfaces';
import { vehicles as vehicleDefs } from '../content/data/vehicles';
import { Hud } from '../ui/hud/hud';
import { applyPose, makeCharacter } from './animstand/character';
import { PlayerAnimator } from './animstand/locomotion';
import { BodyPost } from './player/bodyPost';
import { AnimStand } from './animstand/stand';
import { CameraRig } from './camera/rig';
import { createCtx, probe, type Ctx } from './ctx';
import { Fx } from './fx/fx';
import { Interact } from './interact/interact';
import { Melee } from './melee/melee';
import { Player } from './player/player';
import { Vehicles } from './vehicles/vehicles';
import { Targets } from './weapons/targets';
import { sandboxLoadout, Weapons } from './weapons/weapons';
import { Zombies } from './zombies/zombies';

const easeInOut = (t: number) => t * t * (3 - 2 * t);

/** mechanics-builder: player controller + camera, weapons, melee, zombies, vehicles, interactions, HUD. Fills game.player. */
export async function installGameplay(game: Game): Promise<void> {
  const ctx = createCtx(game) as Ctx;
  ctx.fx = new Fx(game.scene);
  ctx.player = new Player(ctx);
  game.player = ctx.player;
  ctx.rig = new CameraRig(ctx);
  ctx.weapons = new Weapons(ctx);
  ctx.melee = new Melee(ctx);
  ctx.interact = new Interact(ctx);
  ctx.zombies = new Zombies(ctx);
  ctx.vehicles = new Vehicles(ctx);
  ctx.targets = new Targets(ctx);
  ctx.animStand = new AnimStand(ctx);
  ctx.facing = new BodyFacing(ctx);
  ctx.survival = new SurvivalGlue(ctx);
  ctx.hud = new Hud(ctx);

  // Player body (third person)
  const body = await makeCharacter('survivor', 'chr_survivor_a');
  ctx.playerChar = body;
  game.scene.add(body.root);
  const bodyAnim = body.skinned ? new PlayerAnimator(body) : null;
  ctx.playerAnim = bodyAnim;
  const post = bodyAnim ? new BodyPost(ctx, body.root, bodyAnim) : null;
  // Backpack mesh follows the backpack slot (asset node chr_survivor_a_BACKPACK)
  const pack = body.root.getObjectByName('chr_survivor_a_BACKPACK');
  const syncPack = () => {
    const lo = probe(game, 'loadout', 'equipped');
    if (pack && lo) pack.visible = !!lo.equipped('backpack');
  };
  syncPack();
  game.events.on('inventoryChanged', syncPack);
  game.events.on('playerRespawned', syncPack);
  // Near-body fade (QA-064): a dithered (alphaHash, stays in the opaque pass) copy of each body material. A hidden
  // warm-up mesh keeps it in the scene so game.start()'s compileAsync builds the pipeline (no first-use hitch).
  const fadeMats = new Map<Material, Material>();
  const bodyMeshes: Mesh[] = [];
  body.root.traverse((o) => {
    const m = o as Mesh;
    if (!m.isMesh || Array.isArray(m.material)) return;
    let f = fadeMats.get(m.material);
    if (!f) {
      f = m.material.clone();
      Object.assign(f, { alphaHash: true, opacity: cameraFeel.bodyFadeOpacity });
      fadeMats.set(m.material, f);
    }
    bodyMeshes.push(m);
    m.userData.solid = m.material;
    m.userData.faded = f;
  });
  for (const f of fadeMats.values()) {
    const warm = new Mesh(new BoxGeometry(0.01, 0.01, 0.01), f);
    warm.position.set(0, -50, 0);
    game.scene.add(warm);
  }
  let faded = false;
  await ctx.weapons.init(body.hand);
  await Promise.all([ctx.targets.init(), ctx.vehicles.init(), ctx.animStand.init()]);

  const perf = game.perf;
  let wasDead = false;
  // Music intensity (engine/atmosphere.ts): zombies hunting the player nearby, or damage taken in the last few seconds.
  let hurtT = 0;
  game.events.on('playerDamaged', () => (hurtT = atmosphere.music.hurtDangerS));
  const danger = (dt: number) => {
    hurtT = Math.max(0, hurtT - dt);
    if (hurtT > 0) return 1;
    const r2 = atmosphere.music.dangerRadius ** 2;
    let d = 0;
    for (const z of ctx.zombies.list) {
      if (!z.alive || z.sense.awareness === 'unaware' || z.curr.distanceToSquared(ctx.player.position) > r2) continue;
      if (z.sense.awareness === 'detected') return 1;
      d = 0.6;
    }
    return d;
  };
  game.addSystem({
    name: 'sim',
    step(dt) {
      if (game.input.justPressed('F1') || game.input.justPressed('Escape')) ctx.hud.helpOpen = false;
      ctx.survival.step(dt);
      ctx.player.step(dt);
      ctx.rig.step(dt);
      ctx.melee.step(dt);
      ctx.weapons.step(dt);
      ctx.facing.step(dt);
      ctx.interact.step(dt);
      ctx.vehicles.step(dt);
      ctx.targets.step(dt);
      ctx.hud.step();
    },
  });
  game.addSystem({
    name: 'ai',
    step(dt) {
      ctx.zombies.step(dt);
      game.audio.setDanger(danger(dt));
      ctx.controls.endTick();
    },
    render(alpha, dt) {
      perf.begin('anim');
      ctx.player.render(alpha);
      ctx.vehicles.render(alpha);
      ctx.rig.render(alpha, dt);
      ctx.weapons.render(alpha, dt);
      ctx.zombies.render(alpha, dt);
      ctx.targets.render();
      ctx.animStand.render(alpha, dt);
      perf.end('anim');
      perf.begin('fx');
      ctx.fx.update(dt, game.camera.position);
      perf.end('fx');
      perf.begin('anim');
      // Player body
      const p = ctx.player;
      body.root.visible = !p.inVehicle && !ctx.rig.firstPerson;
      const near = body.root.visible && game.camera.position.distanceTo(p.eye) < cameraFeel.bodyFadeDist;
      if (near !== faded) {
        faded = near;
        for (const m of bodyMeshes) m.material = m.userData[near ? 'faded' : 'solid'];
      }
      body.root.position.copy(p.position);
      body.root.rotation.set(0, ctx.facing.render(alpha), 0);
      const pose = body.pose;
      pose.walkPhase = p.stepPhase;
      pose.speed01 = p.speed01;
      pose.crouch = p.crouchT;
      pose.lean = p.sprinting ? 0.2 : 0;
      pose.swing = ctx.melee.swingVis * (ctx.melee.phase === 'idle' ? 0 : 1);
      pose.armRaise = ctx.melee.phase === 'shove' ? Math.max(0, ctx.melee.swingVis) : 0;
      if (p.dead && !wasDead) bodyAnim?.oneshot('death', 0.1, true);
      if (!p.dead && wasDead) bodyAnim?.base('idle', 0.1, 1, true);
      wasDead = p.dead;
      if (p.dead && !bodyAnim) {
        body.root.rotateX(-1.4);
        pose.hold = 'none';
      }
      if (bodyAnim) {
        // Locomotion input for the blend space (body frame), carry state for the upper layer
        const w = ctx.weapons;
        const gun = !!w.item && !w.isMelee && !p.dead && !p.inVehicle;
        const raise = gun ? easeInOut(w.raiseT) : 0;
        const by = body.root.rotation.y;
        const sp = Math.hypot(p.vel.x, p.vel.z);
        const inp = bodyAnim.input;
        inp.speed = p.grounded ? sp : 0;
        inp.dirX = sp > 0.05 ? (p.vel.x * Math.cos(by) - p.vel.z * Math.sin(by)) / sp : 0;
        inp.dirY = sp > 0.05 ? (-p.vel.x * Math.sin(by) - p.vel.z * Math.cos(by)) / sp : 1;
        inp.crouched = p.crouched;
        inp.sprinting = p.sprinting;
        inp.strafe = gun && raise > 0.3;
        inp.carry = gun && w.switchPhase === 'none' && !p.usingItem ? (w.def?.class === 'pistol' ? 'pistol' : 'rifle') : 'none';
        inp.raise = raise;
        bodyAnim.update(dt);
        post?.apply(dt, by, raise);
      } else applyPose(body);
      perf.end('anim');
      perf.begin('ui');
      ctx.hud.render(dt);
      perf.end('ui');
    },
  });

  // Casing tink on first ground contact (fx data), FX accessibility settings (menu settings if present)
  ctx.fx.onCasingLand = (p) => ctx.sfx(fxTuning.casingSound, p, { volume: fxTuning.casingVolume, pitchJitter: 0.06 });
  try {
    const st = JSON.parse(localStorage.getItem('pv.settings.v1') ?? '{}') as { reduceFlashing?: boolean; gore?: 'full' | 'reduced' | 'off' };
    if (st.reduceFlashing !== undefined) fxTuning.reduceFlashing = st.reduceFlashing;
    if (st.gore) fxTuning.gore = st.gore;
  } catch {
    /* storage unavailable: defaults */
  }
  installSaves(ctx);
  installDebug(ctx);
  // Playwright / console hooks (pointer lock may not engage under automation)
  (window as unknown as { __gp: unknown }).__gp = {
    ctx,
    /** Live data tables (the same module instances the game uses, also after HMR). */
    data: { horde, zombieDefs, senses, fxTuning, cameraFeel, movement },
    surfaceOf,
    setLook: (yaw: number, pitch: number) => ctx.player.setLook(yaw, pitch),
    look: (dx: number, dy: number) => ((ctx.controls.lookDx += dx), (ctx.controls.lookDy += dy)),
    mouse: (button: 0 | 2, held: boolean) => ctx.controls.press('Mouse' + button, held),
    spawn: (n: number) => ctx.zombies.spawnInPen(n),
    sandbox: () => {
      ctx.weapons.loadout = sandboxLoadout();
      ctx.weapons.sandbox = true;
      ctx.weapons.select(0);
    },
    /** Aim the camera ray at a world point (yaw/pitch from the camera position). */
    aimAt: (x: number, y: number, z: number) => {
      const o = new Vector3();
      const d = new Vector3();
      for (let i = 0; i < 4; i++) {
        ctx.rig.aimRay(o, d);
        const dx = x - o.x, dy = y - o.y, dz = z - o.z;
        ctx.player.setLook(Math.atan2(-dx, -dz), Math.atan2(dy, Math.hypot(dx, dz)));
      }
    },
    /** Look at the nearest living zombie (head height `h`). Returns its distance. */
    faceZombie: (h = 1.2) => {
      const pc = ctx.player.curr;
      let best: (typeof ctx.zombies.list)[number] | null = null;
      for (const z of ctx.zombies.list) if (z.alive && (!best || z.curr.distanceTo(pc) < best.curr.distanceTo(pc))) best = z;
      if (!best) return -1;
      (window as unknown as { __gp: { aimAt(x: number, y: number, z: number): void } }).__gp.aimAt(best.curr.x, best.curr.y + h, best.curr.z);
      return best.curr.distanceTo(pc);
    },
    targets: () => ctx.targets.list.map((t) => ({ id: t.id, d: t.distance, kind: t.kind, pos: new Box3().setFromObject(t.object).getCenter(new Vector3()).toArray() })),
    teleport: (x: number, z: number, yaw = 0) => ctx.player.teleport(new Vector3(x, 0.1, z), yaw),
    state: () => ({
      pos: ctx.player.curr.toArray(),
      hp: ctx.player.hp,
      stamina: ctx.player.stamina.value,
      weapon: ctx.weapons.item?.defId,
      ammo: ctx.weapons.ammo(),
      last: ctx.weapons.lastShot,
      zombies: ctx.zombies.list.map((z) => ({ id: z.id, alive: z.alive, hp: z.hp, aw: z.sense.awareness, mode: z.mode, pos: z.curr.toArray() })),
      car: ctx.vehicles.list[0] ? { pos: ctx.vehicles.list[0].currPos.toArray(), speed: ctx.vehicles.list[0].speed } : null,
      inVehicle: ctx.player.inVehicle,
      cam: ctx.rig.mode,
    }),
  };
}

function installDebug(ctx: Ctx) {
  const d = ctx.debug;
  const p = ctx.player;
  d.toggle('Player', p as unknown as Record<string, unknown>, 'godMode');
  d.action('Player', 'Heal', () => (p.hp = health.max));
  d.action('Player', 'Respawn', () => p.respawn());
  for (const k of ['jog', 'sprint', 'crouch', 'accelTime', 'stopTime', 'sprintRamp', 'airControl', 'jumpVelocity', 'gravity', 'vaultTime'] as const)
    d.tune('Player', movement, k, 0, k === 'gravity' ? 40 : k.endsWith('Time') || k === 'sprintRamp' || k === 'airControl' ? 1 : 12, 0.01);
  for (const k of ['sprintPerSec', 'regenPerSec', 'regenDelay', 'exhaustedUntil'] as const) d.tune('Player', stamina, k, 0, 60, 0.5);
  d.watch('Player', 'state', () => `${p.gait} ${p.grounded ? 'ground' : 'air'} ${p.speed01.toFixed(2)}×jog st ${p.stamina.value.toFixed(0)}${p.stamina.exhausted ? ' EXH' : ''}`);

  d.action('Camera', 'Toggle first / third person', () => (ctx.rig.mode = ctx.rig.mode === 'tp' ? 'fp' : 'tp'));
  d.tune('Camera', cameraFeel, 'fovFp', 70, 100, 1);
  d.tune('Camera', cameraFeel, 'fovHip', 55, 90, 1);
  d.tune('Camera', cameraFeel, 'armHip', 1, 5, 0.05);
  d.tune('Camera', cameraFeel, 'armAds', 0.5, 3, 0.05);
  d.tune('Camera', cameraFeel, 'shoulderX', 0, 1, 0.01);
  d.tune('Camera', cameraFeel, 'shake', 0, 1, 0.05);
  d.toggle('Camera', cameraFeel, 'headBob');
  d.tune('Camera', cameraFeel, 'swayLag', 0.01, 0.2, 0.005);

  const w = ctx.weapons;
  d.action('Weapons', 'Sandbox loadout (infinite mags)', () => {
    w.loadout = sandboxLoadout();
    w.sandbox = true;
    w.select(0);
  });
  d.watch('Weapons', 'held', () => `${w.item?.defId ?? 'empty'} ${w.fireMode()} spread ${w.spread.toFixed(2)}° ${w.sandbox ? '(sandbox)' : ''}`);
  for (const id of Object.keys(weaponDefs)) {
    const def = weaponDefs[id] as unknown as Record<string, unknown>;
    const g = `Weapon ${id}`;
    d.tune(g, def, 'rpm', 60, 1000, 10);
    d.tune(g, def, 'damage', 5, 150, 1);
    d.tune(g, def, 'headMul', 1, 6, 0.1);
    d.tune(g, def, 'muzzleVelocity', 100, 1000, 5);
    d.tune(g, def.recoilPitch as unknown as Record<string, unknown>, '0', 0, 3, 0.05);
    d.tune(g, def.recoilPitch as unknown as Record<string, unknown>, '1', 0, 3, 0.05);
    d.tune(g, def, 'recoilYaw', 0, 1, 0.01);
    d.tune(g, def, 'recoverPerSec', 1, 30, 0.2);
    d.tune(g, def, 'hipSpread', 0, 8, 0.1);
    d.tune(g, def, 'adsSpread', 0, 2, 0.05);
    d.tune(g, def, 'bloomPerShot', 0, 2, 0.05);
    d.tune(g, def, 'adsMs', 50, 600, 10);
    d.tune(g, def, 'reloadTacticalS', 0.5, 4, 0.05);
    d.tune(g, def, 'reloadEmptyS', 0.5, 4, 0.05);
  }
  d.tune('Weapons', ballistics, 'drag', 0, 0.5, 0.01);
  d.tune('Weapons', ballistics, 'bloomRecoverPerSec', 1, 30, 0.5);
  for (const k of ['damage', 'reach', 'windup', 'active', 'recovery', 'hitstopLight', 'hitstopHeavy'] as const) d.tune('Melee', meleeDefs.knife_a as unknown as Record<string, unknown>, k, 0, k === 'damage' ? 150 : 2.5, 0.01);
  d.tune('Melee', shove, 'range', 0.5, 4, 0.1);
  d.toggle('FX', fxTuning as unknown as Record<string, unknown>, 'reduceFlashing');
  d.action('FX', 'Gore: full / reduced / off', () => {
    fxTuning.gore = fxTuning.gore === 'full' ? 'reduced' : fxTuning.gore === 'reduced' ? 'off' : 'full';
    ctx.game.events.emit('toast', { text: `Gore: ${fxTuning.gore}` });
  });
  d.tune('FX', fxTuning as unknown as Record<string, unknown>, 'lightPeak', 0, 8, 0.1);
  d.tune('FX', fxTuning as unknown as Record<string, unknown>, 'tracerSpeed', 100, 600, 10);
  d.tune('Melee', shove, 'coneDeg', 30, 180, 5);

  const z = ctx.zombies;
  d.action('Zombies', 'Spawn 1', () => void z.spawnInPen(1));
  d.action('Zombies', 'Spawn 5', () => void z.spawnInPen(5));
  d.action('Zombies', 'Spawn 20', () => void z.spawnInPen(20));
  d.action('Zombies', 'Kill all', () => z.killAll());
  d.action('Zombies', 'Clear (incl. corpses)', () => z.clear());
  d.toggle('Zombies', horde, 'passive');
  d.toggle('Zombies', horde, 'showSenses');
  d.tune('Zombies', horde, 'attackTokens', 1, 6, 1);
  const zd = zombieDefs.shambler as unknown as Record<string, unknown>;
  d.tune('Zombies', zd, 'chase', 0.5, 5, 0.1);
  d.tune('Zombies', zd, 'hp', 20, 400, 5);
  d.tune('Zombies', zd, 'attackWindup', 0.2, 1.5, 0.05);
  d.tune('Zombies', zd, 'attackDamage', 0, 60, 1);
  d.tune('Zombies', senses, 'coneDeg', 30, 180, 5);
  d.tune('Zombies', senses, 'rangeDay', 5, 60, 1);
  d.watch('Zombies', 'alive', () => `${z.alive} alive, ${z.kills} killed`);

  const v = ctx.vehicles;
  d.action('Vehicle', 'Go to car', () => {
    const car = v.list[0];
    // A step out from the door, so the capsule never pokes into the body
    if (car) p.teleport(car.doorPos.clone().sub(car.currPos).setY(0).setLength(1.8).add(car.currPos).setY(car.currPos.y + 0.1), 0);
  });
  d.action('Vehicle', 'Flip / reset car', () => v.list[0] && v.flipReset(v.list[0]));
  d.action('Vehicle', 'Repair car', () => v.list.forEach((c) => (c.hp = c.def.hp)));
  d.watch('Vehicle', 'car hp', () => v.list.map((c) => `${c.def.id} ${Math.ceil(c.hp)}`).join(', '));
  for (const def of Object.values(vehicleDefs)) {
    const r = def as unknown as Record<string, unknown>;
    for (const k of ['engineForce', 'brakeForce', 'frictionSlip', 'suspensionStiffness', 'suspensionDamping', 'maxSteerDeg', 'steerAtSpeed', 'topSpeedKmh', 'camDist', 'camHeight'] as const)
      d.tune(`Vehicle ${def.id}`, r, k, 0, k === 'engineForce' ? 20000 : k === 'brakeForce' ? 400 : k === 'topSpeedKmh' ? 200 : k === 'steerAtSpeed' ? 1 : 60, 0.05);
  }
  // Station teleports live in the engine's F1 → Teleport group (QA-025: no duplicates here)
}

declare module './ctx' {
  interface Ctx {
    playerChar: import('./animstand/character').Character;
    playerAnim: PlayerAnimator | null;
  }
}
